import { CSSProperties, useCallback, useMemo } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { Comment, CommentSummary } from '../../../../../store/comments'
import store, { AllState } from '../../../../../store'
import { useSelector } from 'react-redux'
import { List, Map } from 'immutable'
import { closeComment, openComment } from '../../../../../store/information'
import CommentHeaderWbsItem, {
  mapRowDataForCommentHeader,
  WbsItemForCommentHeader,
} from '../../../Comment/CommentHeaderWbsItem'
import { getWbsItemFunctionUuid } from '../../../../../lib/functions/wbsItem'
import { FontSize, TextColor } from '../../../../../styles/commonStyles'
import { colorPalette } from '../../../../style/colorPalette'
import ChatBubbleIcon from '@mui/icons-material/ChatBubble'
import styled from '@emotion/styled'

enum CommentIconColor {
  CLOSED_BACKGROUND = '#FBE6E8',
  OPENED_BACKGROUND = '#EC949C',
}

const CommentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 18px;
  width: 100%;
  cursor: pointer;
  z-index: 1;
`

const StyledChatBubbleIcon = styled(ChatBubbleIcon)`
  transform: scale(1.7, 1.4);
`

type CommentCounterProps = {
  wbsItem: WbsItemForCommentHeader
  numberOfComments: number | undefined
  style?: CSSProperties
}

type CommentCounterStateProps = {
  currentDataUuid?: string
  comments: Map<string, List<Comment>>
}

const CommentCounter = ({
  wbsItem,
  numberOfComments,
  style,
}: CommentCounterProps) => {
  const { currentDataUuid, comments } = useSelector<
    AllState,
    CommentCounterStateProps
  >(state => ({
    // Used to open and close comments
    currentDataUuid: state.information.commentProps?.dataUuid,
    // Used to update numberOfComments when a comment is added or deleted in the discussion on this screen
    comments: state.comments.comments,
  }))

  // comment
  const commentOpened = useMemo(
    () => !!currentDataUuid && currentDataUuid === wbsItem?.uuid,
    [currentDataUuid, wbsItem?.uuid]
  )

  const formatedNumberOfComments = useMemo(() => {
    const commentsInThisWbsItem = wbsItem
      ? comments.get(getWbsItemFunctionUuid() + '-' + wbsItem.uuid)
      : undefined

    // state.comments.comments is set only if comments are displayed,
    // so get from valueGetter too
    const numOfComments = commentsInThisWbsItem
      ? commentsInThisWbsItem?.size || 0
      : numberOfComments || 0
    return 0 < numOfComments
      ? numOfComments >= 100
        ? '99+'
        : `${numOfComments}`
      : undefined
  }, [comments, wbsItem, numberOfComments])

  const onCommentClick = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      if (commentOpened) {
        store.dispatch(closeComment())
      } else {
        store.dispatch(
          openComment({
            applicationFunctionUuid: getWbsItemFunctionUuid()!,
            dataUuid: wbsItem?.uuid,
            projectUuid: wbsItem?.projectUuid,
            headerComponents: [
              <CommentHeaderWbsItem
                key={1}
                wbsItem={mapRowDataForCommentHeader(wbsItem)}
              />,
            ],
          })
        )
      }
    },
    [commentOpened, wbsItem]
  )

  if (!formatedNumberOfComments) {
    return (
      <CommentContainer style={style} onClick={onCommentClick}>
        <StyledChatBubbleIcon
          className="sevend-ag-cell-detail__chat-icon"
          style={{ fill: '#9FACC34D' }}
        />
      </CommentContainer>
    )
  }

  return (
    <CommentContainer
      style={{
        ...style,
        color: commentOpened ? TextColor.WHITE : colorPalette.pink[6],
        borderRadius: '4px',
        fontFamily: 'Noto Sans JP',
        fontWeight: 700,
        lineHeight: '10px',
        fontSize: FontSize.SMALL,
      }}
      onClick={onCommentClick}
    >
      <StyledChatBubbleIcon
        className="sevend-ag-cell-detail__chat-icon"
        style={{
          fill: commentOpened
            ? CommentIconColor.OPENED_BACKGROUND
            : CommentIconColor.CLOSED_BACKGROUND,
          position: 'absolute',
        }}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          paddingBottom: '2px',
        }}
      >
        {formatedNumberOfComments}
      </div>
    </CommentContainer>
  )
}

interface Props extends ICellRendererParams {
  getCommentSummary: (row) => CommentSummary | undefined
  getWbsItem: (row) => any | undefined
}

export const ProjectPlanCommentCellRenderer = ({
  data,
  getWbsItem,
  getCommentSummary,
}: Props) => {
  const wbsItem = getWbsItem(data)
  const commentSummary = getCommentSummary(data)
  const numberOfComments = commentSummary?.numberOfComments || 0

  if (!wbsItem) return <></>
  return (
    <div className="sevend-ag-cell-detail">
      <CommentCounter wbsItem={wbsItem} numberOfComments={numberOfComments} />
    </div>
  )
}
